import React, { useState } from 'react'
import {
  Button,
  ToastNotifications,
  TextAreaInput,
  Modal,
  ModalHeader,
  ModalContent,
  ModalActions,
  Box,
  Text,
  InputCharLimit,
} from '@lightspeed/design-system-react'
import { CustomerBadge, PartialCustomer } from '@vend/react-utilities'
import { ApiCustomer } from '@vend/types'
import { useT } from '@transifex/react'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import {
  CustomerInfo,
  PartialCustomerGroup,
  CUSTOMER_NAME_FIELD,
} from '../../../common/types'
import { STEPS } from '../types'
import { trimContactInfo } from '../../../common/customer-contact-utils'
import {
  useSaleInventoryData,
  useSaleManager,
  useCustomerManager,
  useVdFeatureManager,
  useSaleTracker,
  useRegisterManager,
} from '../../../../../../ng-compat/hooks'
import { EVENT } from '../../../../../../constants'
import {
  setFulfilmentDetails,
  setFulfillmentOutletID,
  setSaleNote,
  updateLineItems,
  selectSaleNote,
  selectLineItems,
  selectSaleLocalId,
  selectSaleCustomer,
  setSaleFulfilmentDetail,
  selectSaleFulfilmentDetail,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import {
  FulfilmentType,
  isNoteInvalid,
} from '../../../../../../models/fulfilment'
import { LineItemFulfilmentNote } from '../../../../../sale/sale-line-item/fulfilment/LineItemFulfilmentNote'
import {
  THRESHOLD_SALE_NOTE_MAX_CHAR,
  SALE_NOTE_MAX_CHAR,
} from '../../../service-modal/constants'

const CAN_NOT_SAVE_CUSTOMER = 'can not save customer'

interface Props {
  onStepChange: any
  onClose: (hasMarked: boolean) => void
  handleSaleNoteChange: (note: string) => void
  type: FulfilmentType
  newCustomer: CustomerInfo | null
  selectedCustomer: ApiCustomer | null
  group?: PartialCustomerGroup
  fulfillmentOutletID: string | null
  contactChanged: boolean
  note: string
  totalQuantity: number
  isFulfilmentNote: boolean
}

// Convert added Customer to ApiCustomer format
const convertCustomerToApiCustomer = (
  customer: CustomerInfo | null,
  group: PartialCustomerGroup
) => {
  if (customer) {
    const result = {
      first_name: customer.firstName && customer.firstName.trim(),
      last_name: customer.lastName && customer.lastName.trim(),
      mobile: customer.mobile,
      email: customer.email,
      phone: customer.phone,
      fax: customer.fax,
      customer_group_id: group.id,
    } as ApiCustomer
    trimContactInfo(result)
    return result
  }
  return null
}

export const Step3: React.FC<Props> = ({
  onStepChange,
  onClose,
  handleSaleNoteChange,
  type,
  newCustomer,
  selectedCustomer,
  group,
  fulfillmentOutletID,
  contactChanged,
  note,
  totalQuantity,
  isFulfilmentNote,
}) => {
  const saleManager = useSaleManager()
  const customerManager = useCustomerManager()
  const saleInventoryData = useSaleInventoryData()
  const vdFeatureManager = useVdFeatureManager()
  const saleTracker = useSaleTracker()
  const dispatch = useAppDispatch()
  const fulfilmentDetail = useAppSelector(selectSaleFulfilmentDetail)
  const lineItems = useAppSelector(selectLineItems)
  const currentSaleNote = useAppSelector(selectSaleNote)
  const localId = useAppSelector(selectSaleLocalId)
  const saleCustomer = useAppSelector(selectSaleCustomer)
  let customerCreated = false

  const shouldShowGroup = vdFeatureManager.get('customer_groups')
  const shouldSaveSelectedOutletID = vdFeatureManager.get(
    'fulfill_from_another_outlet'
  )

  const [loading, setLoading] = useState<boolean>(false)
  const registerManager = useRegisterManager()
  const register = registerManager.getActiveRegister()
  const customerNote = register.print_note_on_receipt

  const handleMark = () => {
    const customerPromise = selectedCustomer
      ? handleCustomerUpdate
      : handleCustomerCreate

    customerPromise()
      .then(() => {
        markUnfulfilled()

        if (vdFeatureManager.get('fulfillment_lif')) {
          dispatch(
            updateLineItems(
              lineItems.map(item => ({
                ...item,
                fulfilmentType: type,
              }))
            )
          )
          dispatch(
            setFulfilmentDetails([{ type, note: fulfilmentDetail?.note || '' }])
          )
        }

        if (note !== currentSaleNote) {
          if (isFulfilmentNote) {
            dispatch(setSaleFulfilmentDetail({ type, note }))
          } else {
            dispatch(setSaleNote(note))
          }
        }

        saleManager.saveSale()
        saleTracker.addTrackingData({
          customerCreated,
          saleId: localId,
        })

        if (isFulfilmentNote) {
          ToastNotifications.success(
            type === FulfilmentType.DELIVERY
              ? t(
                  '{totalQuantity, plural, one {{totalQuantity} item is marked for delivery} other {All {totalQuantity} items are marked for delivery}}',
                  {
                    _context:
                      'Mark as unfulfilled modal - success notification',
                    totalQuantity,
                  }
                )
              : t(
                  '{totalQuantity, plural, one {{totalQuantity} item is marked for pickup} other {All {totalQuantity} items are marked for pickup}}',
                  {
                    _context:
                      'Mark as unfulfilled modal - success notification',
                    totalQuantity,
                  }
                )
          )
        }

        onClose(true)
      })
      .catch(error => {
        setLoading(false)
        if (error && error !== CAN_NOT_SAVE_CUSTOMER) {
          ToastNotifications.negative(
            'Failed to mark as unfulfilled, please try again.'
          )
        }
      })
  }

  const markUnfulfilled = () => {
    if (type === FulfilmentType.DELIVERY) {
      saleManager.unfulfilledDelivery()
    } else {
      saleManager.unfulfilledPickUp()
    }
    saleInventoryData.emit(EVENT.LINE_ITEM_UPDATE_VALIDATION)

    if (shouldSaveSelectedOutletID && fulfillmentOutletID) {
      dispatch(setFulfillmentOutletID(fulfillmentOutletID))
    }
  }

  const handleCustomerCreate = () => {
    return new Promise<void>((resolve, reject) => {
      const customer = convertCustomerToApiCustomer(newCustomer, group!)
      if (customer) {
        setLoading(true)
        return customerManager
          .saveNewCustomer(customer)
          .then(savedCustomer => {
            customerCreated = true
            return saleManager.addCustomer(savedCustomer)
          })
          .then(() => resolve())
          .catch(() => reject(CAN_NOT_SAVE_CUSTOMER))
      }
    })
  }
  const handleCustomerUpdate = () => {
    return new Promise<void>((resolve, reject) => {
      setLoading(true)
      if (contactChanged) {
        trimContactInfo(selectedCustomer!)
        return customerManager
          .updateCustomer(selectedCustomer!)
          .then(savedCustomer => saleManager.addCustomer(savedCustomer))
          .then(() => resolve())
          .catch(() => reject(CAN_NOT_SAVE_CUSTOMER))
      } else if (saleCustomer && saleCustomer.id === selectedCustomer!.id) {
        // Didn't change pre-attached customer
        return resolve()
      } else {
        // Selected/changed a customer at Step2, but didn't update contact info
        return saleManager.addCustomer(selectedCustomer!).then(() => resolve())
      }
    })
  }

  const customer = newCustomer && {
    name: `${newCustomer![CUSTOMER_NAME_FIELD.FIRSTNAME] || ''} ${
      newCustomer![CUSTOMER_NAME_FIELD.LASTNAME] || ''
    }`,
    ...newCustomer,
  }

  const t = useT()

  return (
    <Modal size="small" onClose={() => onClose(false)}>
      <StepBreadcrumbs
        step={STEPS.DETAILS}
        isAddingCustomer={Boolean(newCustomer)}
      />
      {isFulfilmentNote ? (
        <ModalHeader>
          {t(
            '{totalQuantity, plural, one {Add fulfillment details for the {totalQuantity} item} other {Add fulfillment details for these {totalQuantity} items}}',
            {
              _context: 'Mark as unfulfilled modal - header',
              totalQuantity,
            }
          )}
          <Text tag="p" className="vd-mt2">
            {t(
              '{totalQuantity, plural, one {Record the fulfillment details for the item in the fulfillment note.} other {Record the fulfillment details for these items in the fulfillment note.}}',
              {
                _context: 'Mark as unfulfilled modal - subheader',
                totalQuantity,
              }
            )}
          </Text>
        </ModalHeader>
      ) : (
        <ModalHeader>
          Add{type === FulfilmentType.DELIVERY ? ' shipping ' : ' pickup '}
          details for this sale
          <Text tag="p" className="vd-mt2">
            Record the
            {type === FulfilmentType.DELIVERY
              ? ' delivery address '
              : ' pickup information '}
            in the sale note.
          </Text>
        </ModalHeader>
      )}
      <ModalContent>
        {isFulfilmentNote ? (
          <LineItemFulfilmentNote
            note={note}
            onChange={handleSaleNoteChange}
            isDisabled={loading}
          />
        ) : (
          <>
            <CustomerBadge
              customer={customer as PartialCustomer}
              apiCustomer={selectedCustomer!}
              group={shouldShowGroup ? group : undefined}
              size="small"
            />
            <div className="vd-mt6">
              <span className="vd-text-label vd-mr2">
                {customerNote ? 'Sale Note' : 'Staff Note'}
              </span>
              <span className="vd-text-supplementary">
                {customerNote
                  ? 'Customers will be able to see this note on their receipt.'
                  : 'Customers will not see this note on their receipt.'}
              </span>
            </div>
            <div className="vd-mt3">
              <TextAreaInput
                defaultValue={note}
                rows={5}
                className="vd-input vd-textarea"
                data-testid="sale-note"
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  handleSaleNoteChange(e.target.value)
                }}
                hasError={note.length > SALE_NOTE_MAX_CHAR}
              />
              <InputCharLimit
                value={note}
                limit={SALE_NOTE_MAX_CHAR}
                threshold={THRESHOLD_SALE_NOTE_MAX_CHAR}
              />
            </div>
          </>
        )}
      </ModalContent>
      <ModalActions>
        <Box className="vd-btn-group">
          <Button
            onClick={() =>
              onStepChange(newCustomer ? STEPS.ADD : STEPS.CUSTOMER)
            }
            variant="supplementary"
            disabled={loading}
          >
            {t('Back', {
              _context: 'Mark as unfulfilled modal - back button',
            })}
          </Button>
          <Button
            onClick={() => handleMark()}
            variant="go"
            loading={loading}
            disabled={
              isFulfilmentNote
                ? isNoteInvalid(note)
                : note.length > SALE_NOTE_MAX_CHAR
            }
          >
            {isFulfilmentNote
              ? t('Mark items for fulfillment', {
                  _context: 'Mark as unfulfilled modal - next button',
                })
              : t('Mark as unfulfilled', {
                  _context: 'Mark as unfulfilled modal - next button',
                })}
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}
